import React from "react"
import { useTranslation } from "react-i18next"

import FormElement from "components/Form/FormElement"
import { FormError } from "models/form"
import { LocalEntity } from "models"

type BikeCollectionImportFormProps = {
	partner: LocalEntity
	setUpdatedPartner: React.Dispatch<React.SetStateAction<LocalEntity>>
	updatePartner: LocalEntity
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>
	isEdit: boolean
	showLocalErrors: boolean
	isBCSelected: boolean
	hasFleetSelected: boolean
}

const defaultFormFields = {
	import_allowed: false,
	est_bicytrust: false,
}

export const BikeCollectionImportForm = ({
	partner,
	setUpdatedPartner,
	setErrors,
	isEdit,
	showLocalErrors,
	isBCSelected,
	hasFleetSelected,
}: BikeCollectionImportFormProps) => {
	const { t } = useTranslation()
	const [formFields, setFormFields] = React.useState<Partial<LocalEntity>>(defaultFormFields)

	React.useEffect(() => {
		// When partner reloaded (after await fetchData()), set data in formFields
		if (partner || !isEdit) setFormFields(partner)
	}, [partner, isEdit])

	//Effect Listner when hasFleetSelected only when user edit
	React.useEffect(() => {
		if (hasFleetSelected && isEdit) {
			setFormFields((prev) => ({ ...prev, import_allowed: true }))
		}
	}, [hasFleetSelected])

	React.useEffect(
		() => {
			// When we create a new partner, we have to pass data to parent directly.
			// We don't have any submit button on this component in this case
			setUpdatedPartner((prevFormFields) => ({
				...prevFormFields,
				import_allowed: formFields.import_allowed,
				est_bicytrust: formFields.est_bicytrust,
			}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formFields]
	)

	const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.value === "true"
		const name = target.name

		setFormFields((s) => ({
			...s,
			[name]: value,
		}))
	}, [])

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s: Array<FormError>) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<section id="specification" className="py-16 px-4 scroll-mt-10">
			<h2 className="c-h2 font-semibold mb-7 uppercase">
				{t("partner_form.specificFeature", { ns: "partner" })}
			</h2>
			<div className="l-grid u-mg-negative-hz-m">
				<div className="lg:w-1/2 w-full p-5">
					<FormElement
						value={formFields.import_allowed}
						name="import_allowed"
						type="radio"
						label={t("edit.bike_collection_import_label", { ns: "partner" })}
						options={[
							{ value: true, label: t("global.yes") },
							{ value: false, label: t("global.no") },
						]}
						classNameOptionsContainer="flex-row w-full"
						classNameLabel="mb-5"
						classNameOption="flex-1 items-center"
						required={true}
						showErrors={showLocalErrors}
						onValidate={handleValidation}
						onChange={handleInputChange}
						readonly={!isEdit}
					/>
				</div>

				{isBCSelected && (
					<div className="lg:w-1/2 w-full p-5">
						<FormElement
							value={formFields.est_bicytrust}
							name="est_bicytrust"
							type="radio"
							label={t("edit.bicytrust_service", { ns: "partner" })}
							options={[
								{ value: true, label: t("global.yes") },
								{ value: false, label: t("global.no") },
							]}
							classNameOptionsContainer="flex-row w-full"
							classNameLabel="mb-5"
							classNameOption="flex-1 items-center"
							required={true}
							showErrors={showLocalErrors}
							onValidate={handleValidation}
							onChange={handleInputChange}
							readonly={!isEdit}
						/>
					</div>
				)}
			</div>
		</section>
	)
}
